<!-- views/admin/children/Schedule.vue -->
<template>
  <div class="schedule-config">
    <!-- 头部操作栏 -->
    <div class="header">
      <h1>赛制赛程配置</h1>
      <div class="header-actions">
        <button class="btn save-btn" @click="saveConfig" :disabled="isSaving">
          {{ isSaving ? '保存中...' : '保存配置' }}
        </button>
      </div>
    </div>

    <!-- 配置编辑区域 -->
    <div class="config-editor">
      <!-- 板块基础信息 -->
      <div class="config-section">
        <div class="section-header">
          <h2>板块基础信息</h2>
        </div>
        <div class="section-content">
          <div class="form-group">
            <label>板块标题</label>
            <input type="text" v-model="sectionData.title" placeholder="请输入板块标题">
          </div>
          <div class="form-group">
            <label>板块描述</label>
            <textarea v-model="sectionData.desc" placeholder="请输入板块描述"></textarea>
          </div>
        </div>
      </div>

      <!-- 赛程步骤配置 -->
      <div class="config-section">
        <div class="section-header">
          <h2>赛程步骤配置</h2>
        </div>
        <div class="section-content">
          <div
              v-for="(step, index) in sectionData.data.steps"
              :key="index"
              class="step-item"
          >
            <div class="step-header">
              <h3>步骤 {{ index + 1 }} ({{ step.stage }})</h3>
              <button class="remove-btn" @click="removeStep(index)">删除</button>
            </div>

            <div class="form-group">
              <label>阶段名称</label>
              <input type="text" v-model="step.stage" placeholder="请输入阶段名称">
            </div>
            <div class="form-group">
              <label>描述</label>
              <textarea v-model="step.desc" placeholder="请输入描述"></textarea>
            </div>
          </div>

          <button class="add-step-btn" @click="addStep">添加步骤</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getBanner, updateBannerApi } from '@/api/website'

// 板块数据
const sectionData = ref({
  id: "schedule",
  title: "",
  desc: "",
  component: "ScheduleStep",
  data: {
    steps: []
  }
})

const isSaving = ref(false)
const allConfig = ref({}) // 保存完整的配置数据

// 加载配置
const loadConfig = async () => {
  try {
    const response = await getBanner({"adminId":1})
    if (response.data) {
      allConfig.value = response.data.data

      // 查找schedule板块数据
      const scheduleSection = allConfig.value.data.sections.find(section => section.id === "schedule")
      if (scheduleSection) {
        sectionData.value = {
          ...sectionData.value,
          ...scheduleSection
        }
      }
    }
  } catch (error) {
    console.error('加载配置失败:', error)
    alert('加载配置失败，请重试')
  }
}
// 保存配置
const saveConfig = async () => {
  try {
    isSaving.value = true

    // 更新完整配置中的schedule板块数据
    const sectionIndex = allConfig.value.data.sections.findIndex(section => section.id === "schedule")
    if (sectionIndex !== -1) {
      allConfig.value.data.sections[sectionIndex] = {...sectionData.value}
    }
    // 查找并更新导航栏中指向#intro的链接标题
    const navSection = allConfig.value.data.sections.find(section => section.id === "nav")
    if (navSection) {
      const introNavItem = navSection.data.find(item => item.link === "#schedule")
      if (introNavItem) {
        introNavItem.label = sectionData.value.title
      }
    }
    await updateBannerApi(allConfig.value)
    alert('保存成功！')
  } catch (error) {
    console.error('保存配置失败:', error)
    alert('保存失败，请重试')
  } finally {
    isSaving.value = false
  }
}

// 添加步骤
const addStep = () => {
  sectionData.value.data.steps.push({
    stage: "",
    name: "",
    time: "",
    desc: ""
  })
}

// 删除步骤
const removeStep = (index) => {
  if (confirm('确定要删除这个步骤吗？')) {
    sectionData.value.data.steps.splice(index, 1)
  }
}

// 初始化加载配置
onMounted(() => {
  loadConfig()
})
</script>

<style scoped lang="scss">
.schedule-config {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding: 20px;
}

.header {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;

  h1 {
    margin: 0;
    color: #333;
    font-size: 24px;
  }
}

.header-actions {
  display: flex;
  gap: 12px;

  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .save-btn {
    background-color: #E6398B;
    color: white;

    &:hover:not(:disabled) {
      background-color: #d4287a;
    }
  }
}

.config-editor {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.config-section {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  margin-bottom: 16px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-header {
  background-color: #fafafa;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;

  h2 {
    margin: 0;
    font-size: 18px;
    color: #333;
  }
}

.section-content {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    color: #333;
    font-weight: 500;
  }

  input[type="text"],
  input[type="color"],
  select,
  textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    transition: border-color 0.3s;

    &:focus {
      border-color: #1890ff;
      outline: none;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }
  }

  textarea {
    min-height: 80px;
    resize: vertical;
  }
}

.step-item {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.step-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #333;
  }

  .remove-btn {
    padding: 4px 12px;
    background-color: #ff4d4f;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
      background-color: #d9363e;
    }
  }
}

.add-step-btn {
  width: 100%;
  padding: 12px;
  border: 2px dashed #ddd;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.3s;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
    background-color: rgba(24, 144, 255, 0.05);
  }
}
</style>
